<template>
    <div>
        <!-- recipients list -->
        <q-table
            v-if="!isSelectMode"
            @request="onRequest"
            :filter="selectedRecipientsTable.search"
            :rows="selectedRecipients"
            :columns="selectedRecipientsTable.cols"
            v-model:pagination="selectedRecipientsTable.pagination"
            :rows-per-page-options="[
                selectedRecipientsTable.pagination.rowsPerPage
            ]"
            :class="$q.screen.gt.lg ? 'sticky-table recipients-table' : ''"
            class="q-ma-none"
            row-key="id"
            binary-state-sort
            flat
            :no-data-label="$t('No data available')"
            :grid="$q.screen.lt.lg"
        >
            <!-- search bar -->
            <template v-slot:top>
                <div class="col-12">
                    <div v-if="smsDisabled">
                        <div class="text-h5 text-weight-medium">
                            {{
                                $t(
                                    'Receive Email notification for long downtimes on '
                                ) + asset?.name
                            }}
                        </div>
                        <div class="page-description">
                            {{ $t('Configure email alerts for this asset.') }}
                        </div>
                    </div>
                    <div v-else>
                        {{
                            $t(
                                'Receive Email/SMS notification for long downtimes on '
                            ) + asset?.name
                        }}
                    </div>
                </div>
                <div
                    class="row fit q-mb-sm"
                    :class="
                        $q.screen.lt.sm
                            ? ''
                            : 'wrap justify-between items-start content-start'
                    "
                >
                    <q-input
                        data-test="search-recipient-input"
                        v-model="selectedRecipientsTable.search"
                        @clear="removeFilteredRecipientFromStore"
                        :placeholder="$t('Search recipients')"
                        :class="
                            $q.screen.lt.sm ? 'col-12 q-mb-md' : 'col q-mr-md'
                        "
                        debounce="750"
                        clearable
                        outlined
                        dense
                        @input="setFilteredRecipientInStore"
                        color="accent"
                    >
                        <template v-slot:prepend>
                            <q-icon name="search" />
                        </template>
                    </q-input>
                    <q-btn
                        id="add-recipient-button"
                        @click="isSelectMode = true"
                        :label="$t('Add recipient')"
                        :disable="$userCannot('edit', 'recipients')"
                        class="table-header__button"
                        :class="$q.screen.lt.sm ? 'col-12' : ''"
                        color="accent"
                        size="md"
                    ></q-btn>
                </div>
            </template>
            <!-- Email alert?  column -->
            <template v-slot:body-cell-email="props">
                <q-td :props="props">
                    <q-checkbox
                        dense
                        v-model="props.row.email"
                        :disable="!props.row.isEdit"
                        checked-icon="check"
                        color="accent"
                        unchecked-icon="clear"
                    />
                </q-td>
            </template>
            <!-- SMS Alert?  column -->
            <template v-slot:body-cell-sms="props">
                <q-td :props="props">
                    <q-checkbox
                        dense
                        v-model="props.row.sms"
                        :disable="!props.row.isEdit"
                        checked-icon="check"
                        color="accent"
                        unchecked-icon="clear"
                    />
                </q-td>
            </template>
            <template v-slot:body-cell-actions="props">
                <q-td :props="props">
                    <div class="row q-gutter-md">
                        <q-btn
                            v-if="props.row.isEdit"
                            :disable="$userCannot('edit', 'recipients')"
                            @click="rowSave(props.row)"
                            class="table__action-button"
                            color="positive"
                            icon="fas fa-check"
                        >
                            <q-tooltip>{{ $t('Save recipient') }}</q-tooltip>
                        </q-btn>
                        <q-btn
                            v-if="props.row.isEdit"
                            @click="
                                props.row.isEdit = false;
                                props.row.email = props.row['email_tempValue'];
                                props.row.sms = props.row['smsTempValue'];
                            "
                            class="table__action-button"
                            icon="fas fa-times"
                            color="negative"
                            outline
                        >
                            <q-tooltip>{{ $t('Cancel edit') }}</q-tooltip>
                        </q-btn>
                        <q-btn
                            v-if="!props.row.isEdit && !smsDisabled"
                            @click="
                                props.row.isEdit = true;
                                props.row['email_tempValue'] = props.row.email;
                                props.row['smsTempValue'] = props.row.sms;
                            "
                            class="table__action-button"
                            color="accent"
                            icon="fas fa-edit"
                        >
                            <q-tooltip>{{ $t('Edit recipient') }}</q-tooltip>
                        </q-btn>

                        <q-btn
                            id="remove-recipient-button"
                            @click="remove(props.row)"
                            :loading="props.row.isLoading"
                            class="table__action-button"
                            color="negative"
                            icon="fas fa-trash-alt"
                        >
                            <template v-slot:loading>
                                <q-spinner />
                            </template>
                            <q-tooltip>{{ $t('Remove recipient') }}</q-tooltip>
                        </q-btn>
                    </div>
                </q-td>
            </template>
            <!-- No recipients -->
            <template v-slot:no-data>
                <span class="text-weight-regular">
                    {{ `${$t('No recipients are linked to this asset')}.` }}
                    <q-btn
                        @click="isSelectMode = true"
                        :label="$t('Click here to add some.')"
                        class="inline-button"
                        color="accent"
                        dense
                        flat
                        no-caps
                    ></q-btn>
                </span>
            </template>
            <!-- Card template -->
            <template v-slot:item="props">
                <recipient-card
                    :facilityId="facilityId"
                    :userId="props.row.user"
                    :assetId="props.row.asset"
                    :name="props.row.name"
                    :recipientEmail="props.row.user_email"
                    :receiveEmail="props.row.email"
                    :recipientPhoneNumber="props.row.user_phone_number"
                    :receiveSms="props.row.sms"
                    :isEdit="props.row.isEdit"
                    :canEdit="$userCan('edit', 'recipients')"
                    :canDelete="$userCan('edit', 'recipients')"
                    @edit="props.row.isEdit = true"
                    @cancel="props.row.isEdit = false"
                    @save="
                        payload => {
                            save(payload, props.row);
                        }
                    "
                    @delete="remove(props.row)"
                    @triggerReceiveEmail="props.row.email = !props.row.email"
                    @triggerReceiveSms="props.row.sms = !props.row.sms"
                    :smsDisabled="smsDisabled"
                ></recipient-card>
            </template>
        </q-table>

        <!-- Available recipients list -->
        <q-table
            v-else-if="isSelectMode"
            @request="onRequest"
            :filter="availableRecipientsTable.search"
            :rows="availableRecipients"
            :columns="availableRecipientsTable.cols"
            v-model:pagination="availableRecipientsTable.pagination"
            :rows-per-page-options="[
                availableRecipientsTable.pagination.rowsPerPage
            ]"
            v-model:selected="availableRecipientsTable.selected"
            class="sticky-table recipients-table q-ma-none"
            table-style="max-height: none;"
            selection="multiple"
            row-key="id"
            binary-state-sort
            :no-data-label="$t('No data available')"
            flat
        >
            <template v-slot:top>
                <div class="col-12 text-h6 q-mb-md">
                    <q-btn
                        @click="cancelSelectMode"
                        :label="$t('Return to asset recipients')"
                        icon="fas fa-chevron-left"
                        color="primary"
                        flat
                    ></q-btn>
                </div>
                <div class="col-12 text-h6 q-mb-md">
                    {{ $t('Select recipients for this asset') }}
                </div>
                <div
                    class="fit row wrap justify-between items-start content-start"
                >
                    <q-input
                        data-test="search-recipient-input"
                        v-model="availableRecipientsTable.search"
                        @clear="availableRecipientsTable.search = ''"
                        :placeholder="$t('Search users')"
                        class="col q-mr-md"
                        debounce="750"
                        clearable
                        outlined
                        dense
                        color="accent"
                    >
                        <template v-slot:prepend>
                            <q-icon name="search" />
                        </template>
                    </q-input>
                    <div>
                        <q-btn
                            id="confirm-selection-button"
                            @click="confirmSelect"
                            :label="$t('Confirm selection')"
                            class="table-header__button q-mb-md"
                            color="positive"
                            size="md"
                        ></q-btn>
                    </div>
                </div>
            </template>
        </q-table>

        <!-- Recipient Selection detail dialog  -->
        <q-dialog v-model="detailDialog.show">
            <!-- The card displayed when the accesses is empty -->
            <q-card v-if="noneSelectedRecipients">
                <q-card-section>
                    <div class="text-h6">
                        {{ $t('No Selected Recipients') }}
                    </div>
                </q-card-section>

                <q-card-section class="q-pt-none">
                    <p>
                        {{
                            $t(
                                'You have not selected any recipients. Please select at least one to proceed.'
                            )
                        }}
                    </p>
                </q-card-section>
                <q-card-actions align="right">
                    <q-btn
                        flat
                        label="OK"
                        @click="$emit('close', [])"
                        color="primary"
                        v-close-popup
                    ></q-btn>
                </q-card-actions>
            </q-card>
            <q-card
                v-else
                :class="$q.screen.gt.lg ? 'dialog__lg' : 'dialog__xs'"
            >
                <!-- Dialog header -->
                <q-bar class="bg-primary">
                    <div class="text-white">
                        {{ $t('Selected Recipients') }}
                    </div>
                    <q-space />

                    <q-btn
                        @click="closeDetailDialog"
                        class="text-white"
                        icon="close"
                        dense
                        flat
                    >
                        <q-tooltip>{{ $t('Close') }}</q-tooltip>
                    </q-btn>
                </q-bar>

                <!-- Selected Recipients Table -->
                <q-card-section>
                    <div class="col-12 text-grey-9">
                        {{
                            $t(
                                'Select the type of notification each user would like to receive'
                            )
                        }}
                    </div>
                    <q-banner
                        v-if="displayErrors != ''"
                        inline-actions
                        rounded
                        class="bg-red q-mb-md text-white"
                    >
                        <template v-slot:avatar>
                            <q-icon
                                name="fal fa-exclamation-triangle"
                                color="white"
                            />
                        </template>
                        {{ $t('Please fix these errors and try again') }}

                        <template v-slot:action>
                            <q-list dense>
                                <q-item
                                    dense
                                    v-for="value in displayErrors"
                                    :key="value"
                                >
                                    {{ value }}
                                </q-item>
                            </q-list>
                        </template>
                    </q-banner>
                    <q-table
                        :rows="detailDialog.table.selected"
                        :columns="detailDialog.table.columns"
                        :loading="detailDialog.table.isLoading"
                        :no-data-label="$t('No selected recipient available')"
                        v-model:pagination="detailDialog.pagination"
                        :hide-bottom="true"
                        table-style="height: 400px;"
                        row-key="id"
                        binary-state-sort
                        flat
                        bordered
                    >
                        <!-- Email alert column -->
                        <template v-slot:body-cell-receive_email="props">
                            <q-td :props="props">
                                <q-checkbox
                                    dense
                                    v-model="props.row.receive_email"
                                    color="accent"
                                />
                            </q-td>
                        </template>
                        <!-- SMS Alert column -->
                        <template
                            v-if="!smsDisabled"
                            v-slot:body-cell-receive_sms="props"
                        >
                            <q-td :props="props">
                                <q-checkbox
                                    dense
                                    v-model="props.row.receive_sms"
                                    :disable="
                                        props.row.user_phone_number === null
                                    "
                                    checked-icon="check"
                                    color="accent"
                                    unchecked-icon="clear"
                                />
                            </q-td>
                        </template>
                    </q-table>
                </q-card-section>

                <!-- Dialog actions -->
                <q-card-section class="q-px-lg float-right">
                    <q-btn
                        :label="$t('Cancel')"
                        class="q-mr-md"
                        color="white"
                        text-color="primary"
                        @click="closeDetailDialog"
                        outline
                    />

                    <q-btn
                        id="confirm-button"
                        :label="$t('Confirm')"
                        color="primary"
                        @click="submit"
                    />
                </q-card-section>

                <q-inner-loading :showing="detailDialog.isLoading">
                    <q-spinner
                        class="loader"
                        color="primary"
                        size="3em"
                        :thickness="10"
                    />
                    <p>{{ 'Adding recipients' }}</p>
                </q-inner-loading>
            </q-card>
        </q-dialog>
    </div>
</template>

<script>
import UpdateURL from '@mixins/UpdateURL.vue';
import RecipientCard from '@components/RecipientCard.vue';
import { mapActions, mapState } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useFiltersStore } from '@stores/filters.js';

export default {
    name: 'AssetRecipientList',
    components: { RecipientCard },
    mixins: [UpdateURL],
    emits: ['close'],
    props: {
        asset: {
            type: [Object],
            required: true
        },
        facilityId: {
            type: [Number, String],
            required: true
        }
    },
    watch: {
        isSelectMode: {
            handler() {
                this.initRecipientList();
            }
        },
        /**
         * watch for change in toggle state, to validate that
         * both toggles are not false
         */
        selectedRecipients: {
            handler(newValue, oldValue) {
                newValue.forEach((item, i) => {
                    this.validateSelection(item.email, item.sms, i);
                });
            },
            deep: true
        }
    },
    created() {
        // get the store filtered recipient name
        const storedFilteredRecipient = this.getStoredAssetFilter({
            facilityId: this.facilityId,
            assetId: this.asset.id,
            filter: 'recipients'
        });
        // sync the component's search value with the stored
        // fitlered recipient string.
        this.selectedRecipientsTable.search = storedFilteredRecipient;
        // if there is nothing in the store for filtered recipient
        // get all the recipient from the database for this particular
        // asset, otherwise get the recipient related to the stored
        // filtered.
        if (!this.selectedRecipientsTable.search) {
            this.initRecipientList();
        } else {
            this.getRecipients();
        }
    },
    data() {
        return {
            recipients: [],
            selectedRecipients: [],
            availableRecipients: [],
            isSelectMode: false,
            smsDisabled: true,
            // table configs
            selectedRecipientsTable: {
                cols: [
                    {
                        align: 'left',
                        field: 'name',
                        name: 'name',
                        label: this.$t('Name'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'email',
                        name: 'email',
                        label: this.$t('Email alert'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'user_email',
                        name: 'user_email',
                        label: this.$t('Email'),
                        sortable: true
                    },
                    // v-if="!smsDisabled"
                    // {
                    //     align: 'left',
                    //     field: 'sms',
                    //     name: 'sms',
                    //     label: this.$t('SMS alert')
                    // },
                    // {
                    //     align: 'left',
                    //     field: 'user_phone_number',
                    //     name: 'user_phone_number',
                    //     label: this.$t('Phone number'),
                    //     sortable: true
                    // },
                    {
                        align: 'left',
                        field: 'actions',
                        name: 'actions',
                        label: this.$t('Actions')
                    }
                ],
                pagination: {
                    sortBy: '',
                    descending: false,
                    page: 1,
                    rowsPerPage: 30,
                    rowsNumber: null
                },
                search: ''
            },
            // recipients that can be linked to this asset
            availableRecipientsTable: {
                cols: [
                    {
                        align: 'left',
                        field: 'name',
                        name: 'name',
                        label: this.$t('Name'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'user_email',
                        name: 'email',
                        label: this.$t('Email'),
                        sortable: true
                    }
                    // {
                    //     align: 'left',
                    //     field: 'user_phone_number',
                    //     name: 'phone_number',
                    //     label: this.$t('Phone number'),
                    //     sortable: true
                    // }
                ],
                pagination: {
                    sortBy: 'first_name',
                    descending: false,
                    page: 1,
                    rowsPerPage: 30,
                    rowsNumber: null
                },
                search: '',
                selected: []
            },
            //  Recipient Selection detail dialog
            detailDialog: {
                show: false,
                errors: {},
                pagination: {
                    sortBy: '',
                    descending: false,
                    page: 1,
                    rowsPerPage: 0, // infinite
                    rowsNumber: null
                },
                // event table
                table: {
                    selected: [],
                    columns: [
                        {
                            name: 'name',
                            field: 'name',
                            label: this.$t('Name'),
                            align: 'left'
                        },
                        {
                            name: 'email',
                            field: 'user_email',
                            label: this.$t('Email'),
                            align: 'left'
                        },
                        {
                            name: 'receive_email',
                            field: 'receive_email',
                            label: this.$t('Receive email alert?'),
                            align: 'left'
                        }
                        // v-if="!smsDisabled"
                        // {
                        //     name: 'phone_number',
                        //     field: 'user_phone_number',
                        //     label: this.$t('Phone Number'),
                        //     align: 'left'
                        // },
                        // {
                        //     name: 'receive_sms',
                        //     field: 'receive_sms',
                        //     label: this.$t('Receive SMS alert?'),
                        //     align: 'left'
                        // }
                    ],
                    isLoading: false
                }
            }
        };
    },
    computed: {
        ...mapState(useFiltersStore, ['getStoredAssetFilter']),
        /**
         * Base URL to use to fetch recipients.
         * @return {string}
         */
        baseUrl() {
            if (!this.isSelectMode) {
                return '/notification_recipients/';
            } else {
                return `/assets/${this.asset.id}/get_users_with_access/`;
            }
        },
        url() {
            let table = null;
            if (this.isSelectMode) {
                table = this.availableRecipientsTable;
            } else {
                table = this.selectedRecipientsTable;
            }
            // get current pagination state attributes
            const { page, rowsPerPage, sortBy, descending } = table.pagination;
            let sortParam = sortBy;
            if (!this.isSelectMode) {
                sortParam = 'username';
            } else {
                sortParam = `user__${sortParam}`;
            }
            // update URL
            return this.updateURL({
                baseURL: this.baseUrl,
                params: {
                    search: table.search,
                    ordering: descending ? `-${sortParam}` : `${sortParam}`,
                    page: page,
                    // add 'asset' in query if we are in select mode
                    ...(!this.isSelectMode && {
                        asset: this.asset.id
                    })
                }
            });
        },
        /** Add Recipient Screen */
        /**
         * Check if there are selected user.
         * @return {boolean}
         */
        noneSelectedRecipients() {
            return this.availableRecipientsTable.selected.length === 0;
        },
        displayErrors() {
            if (Object.keys(this.detailDialog.errors).length > 0) {
                // let text = '';
                let arr = [];
                for (const key in this.detailDialog.errors) {
                    arr.push(`${key} - ${this.detailDialog.errors[key]}`);
                }
                return arr;
            }
            return '';
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useFiltersStore, ['setAssetFilter']),
        /**
         * Get recipients from the server.
         */
        async getRecipients() {
            // Send the API request only if asset.id is an integer
            if (this.asset) {
                try {
                    let response = await this.$axios.get(this.url);
                    // clear existing data
                    this.selectedRecipients = [];
                    // push data
                    response.data.results.forEach(recipient => {
                        recipient['isEdit'] = false;
                        recipient['name'] =
                            recipient['user_first_name'] +
                            ' ' +
                            recipient['user_last_name'];
                        this.selectedRecipients.push(recipient);
                    });

                    // update total items count for table
                    if (this.isSelectMode) {
                        this.availableRecipientsTable.pagination.rowsNumber =
                            response.data.count;
                    } else {
                        this.selectedRecipientsTable.pagination.rowsNumber =
                            response.data.count;
                    }
                } catch (error) {
                    this.$notifyError(
                        this.$t(
                            'Failed to load recipients. Please try again later.'
                        ),
                        error
                    );
                }
            } else {
                this.$notifyError(
                    this.$t(
                        'Failed to load recipients. Please try again later.'
                    )
                );
            }
            this.stopLoading();
        },
        /**
         * Get Users from the server.
         */
        async getUsers() {
            // Send the API request only if asset.id is an integer
            if (this.asset) {
                try {
                    let response = await this.$axios.get(this.url);
                    // clear existing data
                    this.availableRecipients = [];
                    // push data
                    response.data.results.forEach(user => {
                        if (
                            !this.selectedRecipients.some(
                                recipient => recipient.user === user.user
                            )
                        ) {
                            user['isEdit'] = false;
                            user['name'] =
                                user['user_first_name'] +
                                ' ' +
                                user['user_last_name'];
                            this.availableRecipients.push(user);
                        }
                    });

                    // update total items count for table
                    if (this.isSelectMode) {
                        this.availableRecipientsTable.pagination.rowsNumber =
                            response.data.count;
                    } else {
                        this.selectedRecipientsTable.pagination.rowsNumber =
                            response.data.count;
                    }
                } catch (error) {
                    this.$notifyError(
                        this.$t(
                            'Failed to load recipients. Please try again later.'
                        ),
                        error
                    );
                }
            } else {
                this.$notifyError(
                    this.$t(
                        'Failed to load recipients. Please try again later.'
                    )
                );
            }
            this.stopLoading();
        },
        /**
         * Handle save row.
         * @param {Object}
         */
        async rowSave(row) {
            const payload = {
                user: row.user,
                asset: row.asset,
                email: row.email,
                sms: this.smsDisabled ? false : row.sms
            };
            await this.save(payload, row);
        },
        /**
         * Save and update each recipients.
         * @param {Object} payload
         */
        async save(payload, row) {
            // validate row fields
            if (payload['email'] === false && payload['sms'] === false) {
                if (this.smsDisabled) {
                    this.$notifyError(
                        this.$t(
                            'Email alert must be selected in order to be a recipient'
                        )
                    );
                } else {
                    this.$notifyError(
                        this.$t(
                            'At least one alert option out of SMS or email needs to be selected.'
                        )
                    );
                }
            } else if (payload['sms'] && !row.user_phone_number) {
                this.$notifyError(
                    this.$t(
                        'Cannot enable SMS notifications, please update the phone number in the user profile.'
                    )
                );
            } else {
                // begin loading
                this.beginLoading(`${this.$t('Saving recipient')}...`);
                // send request to server
                try {
                    let response = await this.$axios.put(
                        `/notification_recipients/${row.id}/`,
                        payload
                    );
                    this.$notifySuccess(
                        this.$t('Recipients have been updated.')
                    );
                    // toggle row
                    row.isEdit = false;
                } catch (error) {
                    this.$notifyError(
                        this.$t(
                            'Failed to update recipients in this asset. Please try again later.'
                        ),
                        error
                    );
                } finally {
                    // stop loading
                    this.stopLoading();
                }
            }
        },
        /**
         * Unselect a recipient for this asset.
         */
        async remove(row) {
            this.$confirmDelete(
                this.$t('Are you sure you want to remove') +
                    ` "${row.name}" from "${this.asset.name}"?`
            ).onOk(async () => {
                // begin loading
                row.isLoading = true;
                this.beginLoading(`${this.$t('Removing recipient')}...`);
                try {
                    // unlink recipient from asset
                    let response = await this.$axios.delete(
                        `/notification_recipients/${row.id}/`
                    );
                    // remove item off the table
                    let recipientIndex = this.selectedRecipients.findIndex(
                        item => item.id === row.id
                    );
                    this.selectedRecipients.splice(recipientIndex, 1);

                    // decrement total row count
                    this.selectedRecipientsTable.pagination.rowsNumber--;

                    // notify user
                    this.$notifySuccess(
                        this.$t('Removed recipient from asset')
                    );
                } catch (error) {
                    this.$notifyError(
                        this.$t(
                            'Failed to recipient recipient from this asset. Please try again later.'
                        ),
                        error
                    );
                } finally {
                    row.isLoading = false;
                    // stop loading
                    this.stopLoading();
                }
            });
        },
        /**
         * Fetch data from the server. Triggered by any table sort/search/page changes.
         * @param {Object} props
         * @param {Object} props.pagination
         * @param {string} props.filter
         */
        async onRequest(props) {
            // update pagination object for table
            if (this.isSelectMode) {
                this.availableRecipientsTable.pagination = props.pagination;
            } else {
                this.selectedRecipientsTable.pagination = props.pagination;
            }
            // begin loading
            this.beginLoading(`${this.$t('Loading recipients')}...`);
            // fetch data from the server
            if (this.isSelectMode) {
                await this.getUsers();
            } else {
                await this.getRecipients();
            }
        },
        initRecipientList() {
            // reload list based on view
            let pagination = {};
            if (this.isSelectMode) {
                pagination = this.availableRecipientsTable.pagination;
            } else {
                pagination = this.selectedRecipientsTable.pagination;
            }
            // load data
            this.onRequest({
                pagination: pagination,
                filter: ''
            });
        },
        /**
         * Confirm recipients selected for this asset.
         */
        cancelSelectMode() {
            this.isSelectMode = false;
            this.availableRecipientsTable.selected = [];
        },
        /**
         * Confirm recipients selected for this asset.
         */
        confirmSelect() {
            // extract ids from selected recipients
            const recipientIds = this.availableRecipientsTable.selected.map(
                recipient => recipient.id
            );

            // reset the selected recipient list
            this.detailDialog.table.selected = [];
            this.detailDialog.errors = {};

            // assign the selected recipient to the detail dialog
            this.availableRecipientsTable.selected.forEach(recipient => {
                this.detailDialog.table.selected.push({
                    ...recipient,
                    receive_email: true, // new field
                    receive_sms: false // new field
                });
            });
            // show the detail dialog
            this.detailDialog.show = true;
        },
        /**
         * Validate recipient links
         */
        validateRecipientLinks() {
            this.detailDialog.errors = {};
            let addRecipients = { notification_recipients: [] };
            for (const record of this.detailDialog.table.selected) {
                if (!record['receive_email'] && !record['receive_sms']) {
                    this.detailDialog.errors[record['name']] = '';
                    this.detailDialog.errors[record['name']] = this.$t(
                        'No notification option selected'
                    );
                } else if (
                    record['receive_sms'] &&
                    record['user_phone_number'] === null
                ) {
                    this.detailDialog.errors[record['name']] = '';
                    this.$set(
                        this.detailDialog.errors,
                        record['name'],
                        this.$t(
                            'Please add a phone number to receive SMS notifications'
                        )
                    );
                } else {
                    const payload = {
                        user: record['user'],
                        asset: this.asset.id,
                        email: record['receive_email'],
                        sms: this.smsDisabled ? false : record['receive_sms']
                    };
                    addRecipients['notification_recipients'].push(payload);
                }
            }
            return addRecipients;
        },
        /**
         * Handle submission of recipients links.
         */
        async submit() {
            this.detailDialog.errors = {};
            let addRecipients = this.validateRecipientLinks();

            if (
                addRecipients['notification_recipients'].length ===
                this.detailDialog.table.selected.length
            ) {
                // begin loading
                this.detailDialog.isLoading = true;

                try {
                    // send request to server
                    let response = await this.$axios.post(
                        '/notification_recipients/bulk_add_notification_recipients/',
                        addRecipients
                    );
                    this.$notifySuccess(
                        this.$t('Added recipients to') + ' ' + this.asset.name
                    );
                    this.closeDetailDialog();
                    this.cancelSelectMode();
                } catch (error) {
                    this.$notifyError(
                        this.$t(
                            'Failed to update recipients in this asset. Please try again later.'
                        ),
                        error
                    );
                }

                this.detailDialog.isLoading = false;
            }
        },
        /**
         * Close and reset the detail dialog.
         */
        closeDetailDialog() {
            this.detailDialog.show = false;
            this.detailDialog.table.selected = [];
        },
        /**
         * Update the filtered recipient per asset in a particular facility,
         * in the store.
         * Depends on the value of selectedRecipientsTable.search.
         */
        setFilteredRecipientInStore() {
            this.setAssetFilter({
                facilityId: this.facilityId,
                assetId: this.asset.id,
                filter: 'recipients',
                value: this.selectedRecipientsTable.search
            });
        },
        /**
         * Empty the filtered recipient per asset in a particular facility,
         * in the store.
         * Empty the value of selectedRecipientsTable.search.
         */
        removeFilteredRecipientFromStore() {
            this.setAssetFilter({
                facilityId: this.facilityId,
                assetId: this.asset.id,
                filter: 'recipients',
                value: this.selectedRecipientsTable.search
            });
            this.selectedRecipientsTable.search = '';
        },
        /**
         * Handle validation of email and sms toggles
         * If user sets both toggles to false, an error
         * message is shown and the toggles are reset to
         * the initial state
         */
        validateSelection(email, sms, i) {
            if (!email && !sms) {
                this.$notifyError(
                    this.$t(
                        'At least one alert option out of SMS or email needs to be selected. Please delete the recipient if you do not want any alerts.'
                    )
                );
                this.selectedRecipients[i].email =
                    this.selectedRecipients[i].email_tempValue;
                this.selectedRecipients[i].sms =
                    this.selectedRecipients[i].smsTempValue;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.dialog__lg {
    width: 50%;
    max-width: none;
}

.dialog__xs {
    width: 100%;
    max-width: none;
}

.inline-button :deep(.q-btn__wrapper) {
    padding: 0px;
}

.recipients-table {
    height: 70vh;
}

// align table top with cards
:deep(.q-table__top) {
    padding: 0px;
}
</style>
